/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { forwardRef, useEffect, useImperativeHandle } from 'react'; // we need this to make JSX compile
import { Divider, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Logger } from '../../../../libs/utils/logger';
import FxCard from '../../../Container/FxCard';
import FxCardHeader from '../../../Container/FxCardHeader';
import FxCardBody from '../../../Container/FxCardBody';
import FxMaterialSelect from '../../../Input/FxSelect/FxMaterialSelect';
import FxCardFooter from '../../../Container/FxCardFooter';
import { FxButton } from '../../../Action/FxButton';
import FxLabelView from '../../../Input/FxLabel/FxLabelView';
import { FxTextEdit } from '../../../Input/FxText/FxTextEdit';
import usa_state from '../../../Utils/usa_states.json'
import { AUTH_STRINGS } from '../../../../constants/strings';
import { ReactComponent as MultipleUser } from '../../../../assets/svg/multiple-user-icon.svg';
import { ReactComponent as UnionIcon } from '../../../../assets/svg/Union.svg';
import {
  clean,
  convertToPhoneRequestFormat,
  deleteUnwantedRequest,
  handleAddressChange,
  handleKeyDownChange,
  handleZipChange,
  setPhoneValidationOptions,
  setZipValidationOptions,
  updateComponentParams,
  handleDateValidation,
  setCityValidation,
  fetchDataFromState
} from '../../../Utils/CommonBaseClass';
import { getKey, setKey } from '../../../../libs/utils/storageManager';
import { FxDateEdit } from '../../../Input/FxDate/FxDateEdit';
import FxPhoneEdit from '../../../Input/FxPhone/FxPhoneEdit';
import { useDispatch, useSelector } from 'react-redux';
import { RegisterComponent } from '../../../../libs/saga/dataSaga';
import {  getCustomerUrl, useIsMobileScreen } from '../../../../libs/utils/utils';
import { setLocalStorage } from '../../../../libs/utils/cacheHandler';
import { FieldErrorMessageRender } from '../Common/FieldErrorMessageRender';
import { SignUpIndividualDetailsRef } from '../Individual/SignUpIndividualDetails';
import moment from 'moment';
import FxCheckEdit from '../../../Input/FxCheck/FxCheckEdit';

import HttpClient from '../../../../libs/utils/httpClient';
import { saveNodeData, savePerFieldData } from '../../../../libs/utils/autoSaveField';
const httpClient = HttpClient.getClient();
/**
 * Component: BusinessSignUp
 */


Logger.debug("BusinessDetailsSignup.tsx", "Business Details Signup initializing")

    export const BusinessDetailsSignup = React.memo(forwardRef<SignUpIndividualDetailsRef, any>((props, ref) => {
        const { register, formState: { errors,isValid }, handleSubmit, setValue, clearErrors, setError, control, getValues, reset, resetField } = useForm({mode: 'onChange',reValidateMode:'onChange'});       
        ({ props } = RegisterComponent(props));
        const dispatch = useDispatch();
        const history = useHistory();
        const isSmallScreen = useIsMobileScreen()

        useEffect(() => {
            updateComponentParams(dispatch, props.id, {isLoading: false, isPrimaryAddress: false});

            if(props?.locationData?.length > 0){
                // set the default values for the location form fields
                reset({
                    doingBusinessAs: props?.locationData?.[0]?.doingBusinessAs,
                    locationDetailAddressLine1: !props?.isPrimaryAddress ? props?.locationData?.[0]?.address?.addressLine1 : '',
                    locationDetailAddressLine2: !props?.isPrimaryAddress ? props?.locationData?.[0]?.address?.addressLine2 : '',
                    locationDetailCity: !props?.isPrimaryAddress ? props?.locationData?.[0]?.address?.city : '',
                    locationDetailState: !props?.isPrimaryAddress ? props?.locationData?.[0]?.address?.state : '',
                    locationDetailZip: !props?.isPrimaryAddress ? props?.locationData?.[0]?.address?.zip : '',
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },[]);

        /**
         * This useEffect hook is used to fetch the previously saved data from the GET autoSaved API
         */
        useEffect(() => {
            fetchOnboardingFormData();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },[])

        /**
         * Method to populate the data coming from auto save api 
         * to the form
         * @param setValue 
         * @param autoSavedData 
         */
        function populateBusinessFormFields(setValue: Function, autoSavedData: any) {
            const fieldMap: Record<string, string> = {
              legalName: 'business.legalName',
              ein: 'business.ein',
              phone: 'business.phone',
              email: 'business.email',
              stateOfIncorporation: 'business.stateOfIncorporation',
              dateOfIncorporation: 'business.dateOfIncorporation',
              businessCategory: 'business.businessCategory',
              description: 'business.description',
              addressLine1: 'business.mailingAddress[0].addressLine1',
              addressLine2: 'business.mailingAddress[0].addressLine2',
              city: 'business.mailingAddress[0].city',
              state: 'business.mailingAddress[0].state',
              zip: 'business.mailingAddress[0].zip',
              website: 'business.website',
              locationDetailAddressLine1: 'location.address.addressLine1',
              locationDetailAddressLine2: 'location.address.addressLine2',
              locationDetailCity: 'location.address.city',
              locationDetailState: 'location.address.state',
              locationDetailZip: 'location.address.zip',
              doingBusinessAs: 'location.doingBusinessAs',
            };
          
            Object.entries(fieldMap).forEach(([formField, dataPath]) => {
              setValue(formField, autoSavedData?.[dataPath]);
            });
          }
          

        /**
         * UseEffect to set the values of the fields on the update of the autoSavedData state
         */
        useEffect(() => {
                //Calling the populate method, this basically gets the data from props?.autoSavedData and populates our form
                populateBusinessFormFields(setValue, props?.autoSavedData);

                const isPrimary = checkIfPrimaryAddress({
                    'addressLine1': getValues('locationDetailAddressLine1'),
                    'addressLine2': getValues('locationDetailAddressLine2'),
                    'city': getValues('locationDetailCity'),
                    'state': getValues('locationDetailState'),
                    'zip': getValues('locationDetailZip')
    
                }, {
                    'addressLine1': getValues('addressLine1'),
                    'addressLine2': getValues('addressLine2'),
                    'city': getValues('city'),
                    'state': getValues('state'),
                    'zip': getValues('zip')
                }) ?? false;
                
                updateComponentParams(dispatch, props.id, {isPrimaryAddress: isPrimary});
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },[props?.autoSavedData])

        /**
         * This method fetches the previously saved data from the GET autoSaved API
         * and sets the values in the form fields
         */
        const fetchOnboardingFormData = async () => {
            const previouslySavedData = await getPreviouslySavedDataFromOnboardingApi();

            if (previouslySavedData && previouslySavedData?.data && previouslySavedData?.data?.flatFields) {
                const data = previouslySavedData.data.flatFields;
                updateComponentParams(dispatch, props.id, {autoSavedData: data});
            }
        }

        /**
         * This method compares the location address and the customer address and on the
         * basis of that it returns the boolean value
         * @param item 
         * @param businessDetails 
         * @returns 
         */
        function checkIfPrimaryAddress(item: any, businessDetails: any): boolean {
            if (!item || !businessDetails) return false;
          
            const {
              addressLine1,
              addressLine2,
              city,
              state,
              zip
            } = item;
          
            const {
              addressLine1: busAddressLine1,
              addressLine2: busAddressLine2,
              city: busCity,
              state: busState,
              zip: busZip
            } = businessDetails;
          
            // Normalize and compare
            const normalize = (val: any) => (typeof val === 'string' ? val.trim() : '');
          
            if (!normalize(addressLine1) && !normalize(busAddressLine1)) return false;
          
            return (
              normalize(addressLine1) === normalize(busAddressLine1) &&
              normalize(addressLine2) === normalize(busAddressLine2) &&
              normalize(city) === normalize(busCity) &&
              normalize(state) === normalize(busState) &&
              normalize(zip) === normalize(busZip)
            );
          }
          

        /**
         * Calling the GET autosave api to get the previously saved data
         * @returns 
         */
        const getPreviouslySavedDataFromOnboardingApi = async () => {
            try {
                let apiName: any = '/onboardingFormData'
                const data: any = await httpClient.get(getCustomerUrl(apiName,false)).then(response => {
                    return response
                })
                    .catch((error) => {
                        return { ...error };
                    })
                return data;
            } catch (err) {
                return err;
            }
        }

        /**
         * Exposes the `submitForm` method to parent components via `ref`.
         *
         * - Uses `handleSubmit` (typically from `react-hook-form`) to validate and submit the form.
         * - On successful validation, sets `result` to `{ success: true, errors: {} }`.
         * - On validation failure, sets `result` to `{ success: false, errors: <form errors> }`.
         * - Returns the result object to indicate the outcome of the submission.
         */
        useImperativeHandle(ref, () => ({
            submitForm: async (options: { isClearError?: boolean } = { isClearError: false }) => {
                
                let result:any = {};
                
                await handleSubmit(
                    async (data) => {
                        fetchOnboardingFormData();
                        await onSubmit(data);
                        result = { success: true, data, errors:{}, currentPage: 1 };
                    },
                    (formErrors) => {
                        if(options?.isClearError){
                            clearErrors();
                        }
                        result = { success: false, errors: formErrors, currentPage: 1 };
                    }
                )();
                return result;
            }
        }));

        const categoryData = [
            { value: "PARTNERSHIP", label: " Partnership" },
            { value: "LLC", label: "LLC" },
            { value: "LLP", label: "LLP" },
            { value: "C_CORP", label: "C Corp" },
            { value: "S_CORP", label: "S Corp" },
            { value: "GOVERNMENT_ORGANISATION", label: "Govt. (LocalState/Federal)" },
            { value: "NON_PROFIT", label: "Non Profit" }]

        // ein validation constant
        const setEinValidationOptions = {
            required: true,
            maxLength: 11,
            pattern: {
                value: /^\d{2}-?\d{7}$/,
                message: AUTH_STRINGS.ERRORS.EIN_INVALID_REGEXP,
            },
        };

         // Email validation constant
         const setEmailValidationOptions = {
            required: true,
            maxLength: 250,
            pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,40}$/i,
                message: AUTH_STRINGS.ERRORS.EMAIL_INVALID_REGEXP,
            },
        };

        // function handles the form submit
        async function onSubmit(data: any) {
            let req: any = { ...data }

            //Customer Address
            const address :any = {
                "addressLine1": req['addressLine1'],
                "addressLine2": req['addressLine2'],
                "city": req['city'],
                "state": req['state'],
                "zip": req['zip'],
                "isPrimary": true,
                "usage": {
                    "isPayorAddress": false
                }
            }

            if(data['address_id']){
                address['id'] = data['address_id'];
                delete req['address_id'];
            }

            req['mailingAddress'] = [address]
            req['phone'] = convertToPhoneRequestFormat(req.phone)

            //Location Address
            let locationAddress = {
                "addressLine1": data.locationDetailAddressLine1,
                "addressLine2": data.locationDetailAddressLine2,
                "city": data.locationDetailCity,
                "state": data.locationDetailState,
                "zip": data.locationDetailZip,
            }
            req['address'] = locationAddress;

            req = clean(req);
            const unwantedRequestArray = ['state', 'addressLine1', 'city', 'zip', 'addressLine2','phone-select', 'isPrimaryAddress', 'locationDetailAddressLine1', 'locationDetailAddressLine2', 'locationDetailCity', 'locationDetailState', 'locationDetailZip'];
            deleteUnwantedRequest(req, unwantedRequestArray)            
            setKey('businessDetailsSignupForm','valid');
            if (props.onSubmit) {
                updateComponentParams(dispatch, props.id, { isLoading: true })
                props.onSubmit(data, req,()=>{
                    updateComponentParams(dispatch, props.id, { isLoading: false })
                })
            }
        }

        // Method handles the cancel operation
        const handleClose = () => {
            setLocalStorage('signUpFlow',false);
            history.push('/leads-home')
        }
        // Method handles the onchange event
        const handleChange = (event: any) => {
            if (event.target.name === 'ein') {
                let val = event.target.value.replace(/\D/g, '');
                val = val.replace(/^(\d{2})/, '$1-');
                val = val.replace(/(\d)-(\d{7}).*/, '$1-$2');
                if (val.length === 10) {
                    clearErrors('ein');
                }
                else {
                    setError("ein", {
                        type: "manual",
                        message: "Invalid EIN",
                    });
                }
                setValue('ein', val)
            }
        }

        /**
         * Method to check for a valid website URL using a regex pattern
         * @param e 
         */
        const handleWebsiteValidationsOnInput = (e: any) => {
            const urlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9\-]+\.[a-zA-Z]{2,}([\/\w\-\.\?\=\&\#]*)*\/?$/;
            const value = e.target.value;
            setValue("website", value);
            if (value === "" || urlRegex.test(value)) {
                clearErrors("website"); // clear any previous error
            } else {
                setError("website", {
                    type: "manual",
                    message: "Please enter a valid website URL"
                });
            }
        }

        /**
         * Method fetches data from the state
         */
        const businessState = useSelector((state: any) =>
            fetchDataFromState(state, 'customer-kyc-data')
        );

        /**
         * This method handles the OnChange event of the Date Of incorporation Field
         * @param value 
         */
        const handleDateOfIncorporationValueChange = (value:any) => {
            handleDateValidation("dateOfIncorporation", value, setValue, setError, clearErrors, true, moment(),null,{futureDate:AUTH_STRINGS.ERRORS.BUSINESS_INCORPORATION_FUTURE});
            saveNodeData('company-info-page', 'dateOfIncorporation', value, setValue, getValues,  dispatch, businessState);
        }

        /**
        *Address line 1 validation constant
        **/ 
        const setAddressLine1Validation = (mandatory: boolean) => ({
            required: mandatory,
            minLength: {
                value: 4,
                message: AUTH_STRINGS.ERRORS.ADDRESS_LINE_1_LENGTH_VALIDATION
            },
            maxLength: {
                value: 40,
                message: AUTH_STRINGS.ERRORS.ADDRESS_LINE_1_LENGTH_VALIDATION
            }
        });
        
        /**
         * Checkbox data object for the "Same as Company Legal Address" checkbox    
         */
        const authorizedCheckBoxData = [
            { label: 'Same as Company Legal Address', value: 'isPrimaryAddress', checked: props?.isPrimaryAddress || false }
        ]

        /**
        * handle change for check box
        * @param event :click event
        */
        const handleChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
            const { type, value, checked } = event.target;
            // only care about the “isPrimaryAddress” checkbox
            if (type !== 'checkbox' || value !== 'isPrimaryAddress') return;
            // fields we’ll clear/sync
            const addressFields = [
              'addressLine1',
              'addressLine2',
              'city',
              'state',
              'zip',
            ] as const;
            // 1) clear any prior validation errors
            clearErrors(addressFields);
            // 2) update your component state
            updateComponentParams(dispatch, props.id, { isPrimaryAddress: checked });
            // 3) if checked, copy from the “address…” fields; otherwise reset to empty
            addressFields.forEach((field) => {
              // build “locationDetailXxx” from “addressXxx”
              const targetField =
                'locationDetail' +
                field.charAt(0).toUpperCase() +
                field.slice(1);
              const newValue = checked ? getValues(field) : '';
              setValue(targetField, newValue);
            });

            //saving this data in autosave api for further use
            let requestBody = {
                "location.address.addressLine1": getValues('addressLine1'),
                "location.address.addressLine2": getValues('addressLine2'),
                "location.address.city": getValues('city'),
                "location.address.state": getValues('state'),
                "location.address.zip": getValues('zip'),
            }
            savePerFieldData(requestBody, '/onboardingFormData');
          };

          /**
             * Handles auto-saving of a single form field.
             *
             * This function delegates the save logic to `saveNodeData`, passing in the current field name and value,
             * along with necessary form and state management functions like `setValue`, `getValues`, `dispatch`,
             * and the `individualState`. This ensures that field changes are persisted automatically.
             *
             * @param fieldName - The name of the field being updated.
             * @param value - The new value to be saved.
             */
            const handleAutoSave = (fieldName: string, value: any) => {
                saveNodeData('company-info-page', fieldName, value, setValue, getValues, dispatch, businessState);
            }


        return (
            <Grid xs={12}className='signup-detail-page-bg'>
                <Grid container id="business-details-main-grid" xs={12} justifyContent='center' paddingTop={isSmallScreen?'1rem':'4.5rem'}>
                    <Grid item xs={12} sm={isSmallScreen?undefined:11} md={isSmallScreen?undefined:7}>
                        <FxCard id="business-details-card"  className={isSmallScreen?"fx-theme-passport-no-before fx-no-shadow fx-signup-formkyc fx-business-details":"fx-theme-passport-no-before fx-no-shadow fx-signup-formkyc fx-business-details"}>
                            <div className="fx-form-edit-profile ">
                                <form id="business-details-form" onSubmit={handleSubmit(onSubmit)}>
                                    <FxCardHeader id="business-details-card-header">
                                    <Grid item xs={12} className='fx-signup-formkyc-topblock'>
                                        <Grid item xs={12} container flexWrap={'nowrap'} alignItems={'center'}>
                                        <Grid item className={"fx-signup-formkyc-topblock-icon"}>
                                            <MultipleUser />
                                        </Grid>
                                        <Grid item>
                                            <Typography id="business-details-card-title" variant="h3">
                                            Company Information
                                            </Typography>
                                        </Grid>
                                        </Grid>
                                    </Grid>
                                    </FxCardHeader>
                                    <FxCardBody id="business-details-card-body" className="fx-info-card fx-margin-top-reducer" >
                                        <Grid container direction="row" spacing={2} className="fx-info-card-form">

                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("legalName") }} className={errors.legalName ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="legalName" label="Legal Business Name" name="legalName" variant="outlined" defaultValue={getKey('CustomerName')} isEditable={true} 
                                                onBlur={(e) => handleAutoSave('legalName', e)}/>
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("email") }} control={control} rules={setEmailValidationOptions} className={errors.email ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} id="email" name="email" label="Email" isEditable={true} defaultValue={props.data?.email} 
                                                onBlur={(e) => handleAutoSave('email', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='email' />
                                            </Grid>
                                            
                                            
                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("ein") }} className={errors.ein ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={setEinValidationOptions} onChange={handleChange} onKeyDown={(e: any) => { handleKeyDownChange(e, setValue) }} id="ein-textbox" label="Employer Identification Number (EIN)" name="ein" variant="outlined" placeholder="XX-XXXXXXX" defaultValue={props.data?.ein} 
                                                onBlur={(e) => handleAutoSave('ein', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='ein' />
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxPhoneEdit register={register} control={control} rules={setPhoneValidationOptions(true)} className={errors.phone ? "border-error-input fx-input-edit" : "fx-input-edit"} id="phone" name="phone" label="Phone Number" defaultValue={props.data?.phone} setValue={setValue} setError={setError} clearErrors={clearErrors} 
                                                onBlur={(e) => handleAutoSave('phone', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='phone' />
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("website") }} control={control} className={errors.website ? "border-error-input fx-input-edit" : "fx-input-edit"} id="website" name="website" label="Website (optional)" defaultValue={props.data?.website} onChange={handleWebsiteValidationsOnInput} 
                                                onBlur={(e) => handleAutoSave('website', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='website' />
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxMaterialSelect register={{ ...register("stateOfIncorporation") }} rules={{ required: true }} className={errors.stateOfIncorporation ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} id="stateOfIncorporation" name="stateOfIncorporation" data={usa_state} label="State of Incorporation" setValue={setValue} value={props.data?.stateOfIncorporation ? props.data?.stateOfIncorporation : ''} 
                                                onChange={(e) => handleAutoSave('stateOfIncorporation', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='stateOfIncorporation' /> 
                                            </Grid>

                                            <Grid item xs={12} sm={12} >
                                                <FxDateEdit register={{ ...register("dateOfIncorporation") }} resetField={resetField} className={errors.dateOfIncorporation ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{required:true}} id="dateOfIncorporation" label="Date of Incorporation" name="dateOfIncorporation" setValue={setValue} variant="outlined" defaultValue={props?.autoSavedData && props?.autoSavedData['business.dateOfIncorporation'] ? props?.autoSavedData['business.dateOfIncorporation'] : props.data?.dateOfIncorporation ? props.data?.dateOfIncorporation : ''} disableFuture={true} futureDateMessage={AUTH_STRINGS.ERRORS.BUSINESS_INCORPORATION_FUTURE} onChange={(value: any) => handleDateOfIncorporationValueChange(value)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='dateOfIncorporation' type='date'  /> 
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxMaterialSelect register={{ ...register("businessCategory") }} className={errors.businessCategory ? "border-error-input fx-input-edit" : "fx-input-edit"} rules={{ required: true }} control={control} id="businessCategory" name="businessCategory" data={categoryData} label="Company Type" setValue={setValue} value={props.data?.businessCategory} 
                                                onChange={(e) => handleAutoSave('businessCategory', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='businessCategory' /> 
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("description") }} className={errors.description ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: false }} id="description" label="Company Description (optional)" name="description" variant="outlined" defaultValue={props.data?.description} placeholder='Enter text...'  
                                                onBlur={(e) => handleAutoSave('description', e)}/>
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <Divider />
                                            </Grid>


                                            <Grid item xs={12}>
                                                <Grid item xs={12} container flexWrap={'nowrap'} alignItems={'center'}>
                                                    <Grid item className={"fx-signup-formkyc-topblock-icon"}>
                                                        <UnionIcon />
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography id="business-details-card-title" variant="h3" className='fx-business-address-details-title'>
                                                        Business address details
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("doingBusinessAs") }} className={errors.doingBusinessAs ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="doingBusinessAs" label="Doing Business As" name="doingBusinessAs" variant="outlined" defaultValue={props?.data?.doingBusinessAs} 
                                                onBlur={(e) => handleAutoSave('doingBusinessAs', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='doingBusinessAs' />
                                            </Grid>

                                            <Grid container item xs={12} sm={12} className='fx-business-address-details-legal-address-typography'>
                                                <Grid item xs={12} sm={12}>
                                                    <FxLabelView id="business-details-label2" className="fx-business-address-details-legal-address-typography-title">COMPANY LEGAL ADDRESS</FxLabelView>
                                                </Grid>

                                                <Grid item xs={12} sm={12} marginTop={'1rem'}>
                                                    <Typography id='business-owner-card-form-card-label' className='fx-business-address-details-legal-address-typography-description'>
                                                        The registered address used for tax filings, legal documents, and official government communication.
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <FxTextEdit register={{ ...register("address_id") }} className={"fx-hidden"} control={control} rules={{ required: false }} id="signUp-business-owner-form-card-address-id"  name="address_id" variant="outlined" defaultValue={props.data?.address_id} />

                                            <Grid item xs={12} sm={!isSmallScreen?6:12}>
                                                <FxTextEdit register={{ ...register("addressLine1") }} className={errors.addressLine1 ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"}  rules={setAddressLine1Validation(true)} control={control} id="signUp-addressLine1" label="Address Line 1" name="addressLine1"  onChange={(e: any) => { handleAddressChange(e, setValue, setError, clearErrors) }} defaultValue={props.data?.addressLine1} 
                                                onBlur={(e) => handleAutoSave('addressLine1', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='addressLine1' />
                                            </Grid>

                                            <Grid item xs={12} sm={!isSmallScreen?6:12}>
                                                <FxTextEdit register={{ ...register("addressLine2") }} className={errors.addressLine2 ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={{ required: false }} id="signUp-addressLine2" label="Address Line 2 (optional)" name="addressLine2" defaultValue={props.data?.addressLine2} 
                                                onBlur={(e) => handleAutoSave('addressLine2', e)}/>
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                <FxTextEdit register={{ ...register("city") }} className={errors.city ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={setCityValidation(true)} id="signUp-city" label="City" name="city" defaultValue={props.data?.city} 
                                                onBlur={(e) => handleAutoSave('city', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='city' />
                                            </Grid>

                                            <Grid item xs={12} sm={!isSmallScreen?6:12}>
                                                <FxMaterialSelect register={{ ...register("state") }} rules={{ required: true }} className={errors.state ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} id="signUp-state" name="state" data={usa_state} label="State" setValue={setValue} value={props.data?.state ? props.data?.state : ''} 
                                                onChange={(e) => handleAutoSave('state', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='state' />
                                            </Grid>

                                            <Grid item xs={12} sm={!isSmallScreen?6:12}>
                                                <FxTextEdit register={{ ...register("zip") }} className={errors.zip ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={setZipValidationOptions(true)} id="signUp-zip" label="Zip" name="zip" onChange={(e: any) => { handleZipChange(e, setValue, setError, clearErrors) }} onKeyDown={(e: any) => { handleKeyDownChange(e, setValue) }} defaultValue={props.data?.zip} 
                                                onBlur={(e) => handleAutoSave('zip', e)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='zip' />
                                            </Grid>

                                            <Grid container item xs={12} sm={12} className='fx-business-address-details-legal-address-typography' marginTop={'1.5rem'}>
                                                <Grid item xs={12} sm={12}>
                                                    <FxLabelView id="business-details-label2" className="fx-business-address-details-legal-address-typography-title" >BUSINESS LOCATION ADDRESS</FxLabelView>
                                                </Grid>

                                                <Grid item xs={12} sm={12} marginTop={'1rem'}>
                                                    <Typography id='business-owner-card-form-card-label' className='fx-business-address-details-legal-address-typography-description'>
                                                    The actual place where your business operates and serves customers or clients.
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12} sm={12}>
                                                    <FxCheckEdit register={{ ...register("isPrimaryAddress") }} onChange={handleChangeCheckBox} className='fx-label-kyc' control={control} data={authorizedCheckBoxData} id="signUp-location-detail-form-card-isPrimaryAddress" name="isPrimaryAddress" row="vertical" setValue={setValue} updateOptions={true} />
                                            </Grid>

                                            {!props?.isPrimaryAddress && <>

                                                <Grid item xs={12} sm={12}>
                                                    <FxTextEdit register={{ ...register("locationDetailAddressLine1") }} className={errors.locationDetailAddressLine1 ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"}  rules={props?.isPrimaryAddress ? setAddressLine1Validation(false) : setAddressLine1Validation(true)} control={control} id="signUp-location-detail-addressLine1" label="Address Line 1" name="locationDetailAddressLine1"  onChange={(e: any) => { handleAddressChange(e, setValue, setError, clearErrors) }} isEditable={!props?.isPrimaryAddress} defaultValue={props?.data?.[0]?.address?.addressLine1} 
                                                    onBlur={(e) => handleAutoSave('locationDetailAddressLine1', e)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='locationDetailAddressLine1' />
                                                </Grid>

                                                <Grid item xs={12} sm={12}>
                                                    <FxTextEdit register={{ ...register("locationDetailAddressLine2") }} className={errors.locationDetailAddressLine2 ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={{ required: false }} id="signUp-location-detail-addressLine2" label="Address Line 2 (optional)" name="locationDetailAddressLine2" isEditable={!props?.isPrimaryAddress} defaultValue={props?.data?.[0]?.address?.addressLine2} 
                                                    onBlur={(e) => handleAutoSave('locationDetailAddressLine2', e)}/>
                                                </Grid>

                                                <Grid item xs={12} sm={12}>
                                                    <FxTextEdit register={{ ...register("locationDetailCity") }} className={errors.locationDetailCity ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={props?.isPrimaryAddress ? setCityValidation(true) : setCityValidation(true)} id="signUp-location-detail-city" label="City" name="locationDetailCity" isEditable={!props?.isPrimaryAddress} defaultValue={props?.data?.[0]?.address?.city} 
                                                    onBlur={(e) => handleAutoSave('locationDetailCity', e)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='locationDetailCity' />
                                                </Grid>

                                                <Grid item xs={12} sm={!isSmallScreen?6:12}>
                                                    <FxMaterialSelect register={{ ...register("locationDetailState") }} rules={{ required: props?.isPrimaryAddress ? false : true }} className={errors.locationDetailState ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} id="signUp-location-detail-select-state" name="locationDetailState" data={usa_state} label="State" setValue={setValue} readOnly={props?.isPrimaryAddress} value={props?.data?.[0] ? props?.data?.[0]?.address?.state : ''} 
                                                    onChange={(e) => handleAutoSave('locationDetailState', e)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='locationDetailState' />
                                                </Grid>

                                                <Grid item xs={12} sm={!isSmallScreen?6:12}>
                                                    <FxTextEdit register={{ ...register("locationDetailZip") }} className={errors.locationDetailZip ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={props?.isPrimaryAddress ? setZipValidationOptions(false) : setZipValidationOptions(true)} id="signUp-location-detail-zip" label="Zip" name="locationDetailZip" onChange={(e: any) => { handleZipChange(e, setValue, setError, clearErrors) }} onKeyDown={(e: any) => { handleKeyDownChange(e, setValue) }} isEditable={!props?.isPrimaryAddress} defaultValue={props?.data?.[0]?.address?.zip} 
                                                    onBlur={(e) => handleAutoSave('locationDetailZip', e)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='locationDetailZip' />
                                                </Grid>

                                            </>}

                                            <Grid className={isSmallScreen ? 'fx-margin-top-2' : ''} item xs={12} sm={12}>
                                                <Divider />
                                            </Grid>

                                        </Grid>
                                    </FxCardBody>
                                    <FxCardFooter id="business-details-card-footer" className={isSmallScreen? "fx-sticky-button-form-container fx-footer":"fx-footer"}>
                                        {!isSmallScreen && <Grid container direction="row" justifyContent="flex-start" className="fx-modal-footer fx-footer-kyc">
                                            <Typography id="business-details-card-title"className="fx-footer-kyc-text">   
                                            Your changes are autosaved
                                            </Typography>
                                        </Grid>}

                                        <Grid container direction="row" justifyContent="flex-end" className="fx-modal-footer fx-footer-kyc">

                                            <span className="fx-padding-right-16" />

                                            {getKey('loginType')==='PARTNER' && <FxButton variant="contained"
                                                className="fx-button fx-button-cancel"
                                                id="ea-modal"
                                                onClick={handleClose}>
                                                Go Back
                                            </FxButton>}
                                            <Grid className='fx-padding-right-15'></Grid>
                                            <FxButton
                                                disableRipple={false}
                                                className="fx-button fx-button-theme"
                                                id={"business-details-card-save-button"}
                                                type="submit"
                                                isSubmitting={props?.isLoading}
                                            >
                                                    Continue
                                            </FxButton>

                                        </Grid>
                                    </FxCardFooter>
                                </form>
                            </div>
                        </FxCard>
                    </Grid>
                </Grid>
            </Grid>
        )
    }))